import * as THREE from "three";
import { CustomOrbitControls } from "./custom-orbit-controls";
import { Annotation } from "./annotation";
import { Tools } from "./tools";
import { SceneElement } from "../data-structures/scene-element";
import { ViewType } from "../data-structures/view-type";
import { ElementType } from "../data-structures/element-type";
import { LayerItem } from "../data-structures/layer-item";
import { EventEmitter } from "./event-emitter";
import { LayerItemType } from "../data-structures/layer-item-type";
import { computeBoundsTree, disposeBoundsTree, acceleratedRaycast } from "three-mesh-bvh";
import { Vector2, Vector3 } from "three";
import { Measure } from "./measure";
import { UnifiedLineMeasure } from "./unified-line-measure";
import { Profile } from "./profile";
import { VolumeMetadata } from "@/swagger";
import { ProfileManager } from "./profile-manager";
import { VolumeManager } from "./volume-manager";
import { CadManager } from "./cad-manager";
import { MeshManager } from "./mesh-manager";
import { PointCloudManager } from "./pointcloud-manager";
import { OrthophotoManager } from "./orthophoto-manager";
import type { ExternalImage } from "./external-image";
import { ExternalImageManager } from "./external-image-manager";

declare const Potree: any;
declare const $: any;
declare const proj4: any;

let pointShapeGuardInstalled = false;

function forcePointShapeCircle(): void {
  if (pointShapeGuardInstalled) return;

  const proto = Potree?.PointCloudMaterial?.prototype;
  const descriptor = proto && Object.getOwnPropertyDescriptor(proto, "shape");
  if (!proto || !descriptor?.set) {
    pointShapeGuardInstalled = true;
    return;
  }

  const originalSet = descriptor.set;
  Object.defineProperty(proto, "shape", {
    ...descriptor,
    set() {
      originalSet.call(this, Potree.PointShape.CIRCLE);
    },
  });

  pointShapeGuardInstalled = true;
}

THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
THREE.Mesh.prototype.raycast = acceleratedRaycast;

/**
 * CUSTOM Potree Viewer that inherits from Potree.Viewer implementation and overrides behavior
 * and adds custom methods and functionalities
 */
export class CustomViewer extends (Potree.Viewer as any) {
	// Constants
	private static readonly MAP_COORDINATE_SYSTEM = "EPSG:3857"; // Pseudo mercator - map view space
	private static readonly CAMERA_FOV = 60;

	private currentTool: Tools = Tools.Selection;
	private readonly intersectionPoint = new THREE.Vector3();
	private projectCoordinateSystem: string | null = null; //Project coordinate space
	public readonly mapCoordinateSystem = CustomViewer.MAP_COORDINATE_SYSTEM;
	private readonly raycaster: THREE.Raycaster;
	private readonly startPosition = new THREE.Vector2();
	private readonly mousePosition = new THREE.Vector2();
	private highlightedElement: SceneElement | null = null;
	private readonly domElement: HTMLDivElement;
	private selectedElement: SceneElement | null = null;
	private createdMeasurement: SceneElement | null = null;
	private movingAnnotation: Annotation | null = null;
	private movingSphere: any = null;
	private viewType = ViewType.View3D;
	private readonly customControls: CustomOrbitControls;
	private readonly measurementsGroup: THREE.Group;
	private readonly unifiedLineMeasurementsGroup: THREE.Group;
	private readonly annotationsGroup: THREE.Group;
	public readonly externalImageManager: ExternalImageManager;
	private readonly movementHelper: THREE.Object3D;
	private isPointerDown = false;
	private wasPointerDown = false;
	private wasClicked = false;
	private suppressNextPointerClick = false;
	private wasRightClick = false;
	private wasDoubleRightClick = false;
	private wasDoubleLeftClick = false;
	private previousRightClickTime = 0;
	private unit = "";
	private previousMousePosition = new THREE.Vector2();
	private lastValidIntersectionPoint = new THREE.Vector3();
	
	private smoothIntersectionPoint = new THREE.Vector3();
	private targetIntersectionPoint = new THREE.Vector3();
	private isAnimating = false;
	private raycastPointcloudThisFrame = false;

	private readonly ground: THREE.Plane;
	public readonly volumesGroup: THREE.Group;
	public readonly volumeLabelsGroup: THREE.Group; // Separate group for volume labels to ensure they render on top
	public readonly profilesGroup: THREE.Group;
	private readonly profileManager: ProfileManager;
	private readonly volumeManager: VolumeManager;
	private readonly cadManager: CadManager;
	private readonly meshManager: MeshManager;
	private readonly pointCloudManager: PointCloudManager;
	private readonly orthophotoManager: OrthophotoManager;
	private isMultiSelectKeyPressed = false;

	// Getters for backwards compatibility
	get meshes() { return this.meshManager.meshes; }
	get meshesGroup() { return this.meshManager.meshesGroup; }
	get pointclouds() { return this.pointCloudManager.pointclouds; }
	get orthophotoMaps() { return this.orthophotoManager.orthophotoMaps; }
	get mapType() { return this.orthophotoManager.mapType; }
	get measurements() { return this.cadManager.measurements; }
	get unifiedLineMeasurements() { return this.cadManager.unifiedLineMeasurements; }
	get annotations() { return this.cadManager.annotations; }
	get cadLayers() { return this.cadManager.cadLayers; }
	get activeCadLayer() { return this.cadManager.activeCadLayer; }
	set activeCadLayer(value: LayerItem | null) { this.cadManager.activeCadLayer = value; }
	get profiles() { return this.profileManager.profiles; }
	get selectedProfiles() { return this.profileManager.selectedProfiles; }
	get profileLayers() { return this.profileManager.profileLayers; }
	get activeProfileLayer() { return this.profileManager.activeProfileLayer; }
	set activeProfileLayer(value: LayerItem | null) { this.profileManager.activeProfileLayer = value; }

	public onLayersChanged: EventEmitter = new EventEmitter();
	public onSelectedElementChanged: EventEmitter<any> = new EventEmitter<any>();
	public onToolCancel: EventEmitter = new EventEmitter();
	public onVolumeHover: EventEmitter<string | null> = new EventEmitter<string | null>();
	public onProfileHover: EventEmitter<{ profileId: string; distance: number } | null> = new EventEmitter<{ profileId: string; distance: number } | null>();
	public onExternalImageOpen: EventEmitter<ExternalImage> = new EventEmitter<ExternalImage>();

	get volumes() { return this.volumeManager.volumes; }

	getVolumeChildVisibility(volumeId: string, childType: "before" | "after" | "diff"): boolean {
		return this.volumeManager.getChildVisibility(volumeId, childType);
	}

		toggleVolumeChildVisibility(volumeId: string, childType: "before" | "after" | "diff") {
		this.volumeManager.toggleChildVisibility(volumeId, childType);
	}


	get inputHandler(): any {
		return this._inputHandler;
	}

	set inputHandler(value: any) {
		this._inputHandler = value;
		if (this._inputHandler) {
			//Prevent input handler form handling ANY INTERACTIVE OBJECTS,
			//Editing of existing measurements is implemented in Potreethis.vue in update()
			//Potree implementation is disabled because it only allows point dragging over
			//the pointclouds but not the meshes, therefore new logic had to be implemented
			//that handles both meshes and pointclouds
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			this._inputHandler.registerInteractiveScene = () => {};
		}
	}

	constructor(element, args = {}) {
		super(element, args);
		forcePointShapeCircle();
		this.domElement = element;
		this.raycaster = new THREE.Raycaster();
		this.raycaster.params = {
			Mesh: { treshold: 10000000 },
			Line: { threshold: 1 },
			LOD: {},
			Points: { threshold: 1 },
			Sprite: {},
		};

		(window as any).viewer = this;
		this.customControls = new CustomOrbitControls(this);
		this.setControls(this.customControls);

		this.setFOV(CustomViewer.CAMERA_FOV);
		this.setBackground("none");

		this.ground = new THREE.Plane();
		this.ground.setFromNormalAndCoplanarPoint(new THREE.Vector3(0,0,1), new THREE.Vector3(0,0,0));

		this.loadGUI(() => {
			this.setLanguage("en");
			$("#menu_appearance").next().show();
		});

		const light = new THREE.AmbientLight(0xffffff, 0.6);
		this.scene.scene.add(light);

		// Add directional light for proper shading of MeshLambertMaterial
		const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
		directionalLight.position.set(1, 1, 1);
		this.scene.scene.add(directionalLight);

		this.scene.scene.name = "root_scene";

		this.orthophotoManager = new OrthophotoManager(this);

		this.profilesGroup = new THREE.Group();
		this.profilesGroup.name = "ProfilesGroup";
		this.scene.scene.add(this.profilesGroup);
		this.profileManager = new ProfileManager(this);

		this.volumesGroup = new THREE.Group();
		this.volumesGroup.name = "VolumesGroup";
		this.scene.scene.add(this.volumesGroup);

		// Create separate group for volume labels - added last to ensure they render on top
		this.volumeLabelsGroup = new THREE.Group();
		this.volumeLabelsGroup.name = "VolumeLabelsGroup";
		this.scene.scene.add(this.volumeLabelsGroup);

		this.movementHelper = new THREE.Group();
		this.movementHelper.add(new THREE.AxesHelper(1));
		const axes2 = new THREE.AxesHelper(1);
		axes2.scale.set(-1,-1,-1);
		this.movementHelper.add(axes2);
		this.movementHelper.visible = false;
		this.scene.scene.add(this.movementHelper);

		(window as any).scene = this.scene.scene;

		//Next line fixes a bug related to Firefox loading images from cache on Firefox
		//https://github.com/mrdoob/three.js/pull/22975
		(window as any).createImageBitmap = undefined;

		this.meshManager = new MeshManager(this);
		this.volumeManager = new VolumeManager(this, this.domElement);
		this.cadManager = new CadManager(this);
		this.pointCloudManager = new PointCloudManager(this);

		this.measurementsGroup = new THREE.Group();
		this.measurementsGroup.name = "MeasurementsGroup";
		this.annotationsGroup = new THREE.Group();
		this.annotationsGroup.name = "AnnotationsGroup";
		this.unifiedLineMeasurementsGroup = new THREE.Group();
		this.unifiedLineMeasurementsGroup.name = "UnifiedLineMeasurementsGroup";
		this.scene.scene.add(this.measurementsGroup);
		this.scene.scene.add(this.annotationsGroup);
		this.externalImageManager = new ExternalImageManager({
			viewer: this,
			measurementsGroup: this.measurementsGroup,
			unifiedLineMeasurementsGroup: this.unifiedLineMeasurementsGroup,
			annotationsGroup: this.annotationsGroup,
			mousePosition: this.mousePosition,
			lastValidIntersectionPoint: this.lastValidIntersectionPoint,
			getCurrentTool: () => this.currentTool,
			getOverlayTargets: () => [...this.measurements, ...this.unifiedLineMeasurements, ...this.annotations],
			isCadPointMoveActive: () => this.movingSphere != null,
		});
		this.externalImageManager.setVisible(this.viewType == ViewType.View3D);
		this.scene.scene.add(this.unifiedLineMeasurementsGroup);

		window.addEventListener("pointerdown", this.onPointerDown.bind(this));
		window.addEventListener("pointermove", this.onPointerMove.bind(this));
		window.addEventListener("pointerup", this.onPointerUp.bind(this));
		window.addEventListener("keyup", this.onKeyUp.bind(this));
		window.addEventListener("keydown", this.onKeyDown.bind(this));
		this.addEventListener("render.pass.end", this.externalImageManager.magnifier.render);
		this.addEventListener("render.pass.end", this.externalImageManager.renderOverlayOnTop);
	}

	public updateLayers() {
		this.onLayersChanged.emit();
	}

	public deleteObject(object: any) {
		if (object.elementType) {
			const type = object.elementType as ElementType;
			if (type == "annotation") {
				const annotation = this.annotations.find((x) => x.uuid == object.uuid);
				if (annotation) {
					this.cadManager.removeAnnotation(annotation);
					this.updateLayers();
				}
			} else if (type == "line" || type == "point" || type == "circle") {
				const measure = this.measurements.find((x) => x.uuid == object.uuid);
				if (measure) {
					this.cadManager.removeMeasurement(measure);
				} else {
					const unifiedLineMeasure = this.unifiedLineMeasurements.find((x) => x.uuid == object.uuid);
					if (unifiedLineMeasure) {
						this.cadManager.removeUnifiedLineMeasurement(unifiedLineMeasure);
					}
				}
			} else if ((object.elementType && object.elementType === "volume") || (object.type && object.type === "volume")) {
				const volume = this.volumes.find((x) => x.uuid == object.uuid);
				if (volume) {
					this.removeVolume(volume);
				}
			}
			if (this.selectedElement == object) {
				this.onSelectedElementChanged.emit(null);
			}
		}
	}

	public setMapType(mapType: "satellite" | "vector") {
		this.orthophotoManager.setMapType(mapType);
	}

	public setTool(newTool: Tools) {
		if (newTool == this.currentTool) {
			return;
		}

		if (!this.cadManager) {
			console.warn("CadManager not initialized yet");
			return;
		}

		this.deselectElement();

		//Disable previous tool
		switch (this.currentTool) {
			case Tools.Annotation:
				break;
			case Tools.Point:
			case Tools.Height:
			case Tools.Circle:
			case Tools.Line:
			case Tools.Polygon:
				this.cadManager.stopLineDrawing();
				break;
		}

		this.currentTool = newTool;
		this.externalImageManager.magnifier.updateVisibility();

		//Enable new tool
		switch (this.currentTool) {
			case Tools.Annotation:
				this.createNewAnnotation();
				break;
			case Tools.Line:
				this.cadManager.startLineDrawing();
				break;
			case Tools.Point:
				this.cadManager.startLineDrawing(Tools.Point);
				break;
			case Tools.Polygon:
				this.cadManager.startLineDrawing(Tools.Polygon);
				break;
			case Tools.Height:
				this.cadManager.startLineDrawing(Tools.Height);
				break;
			case Tools.Circle:
				this.cadManager.startLineDrawing(Tools.Circle);
				break;
		}
	}

	public changeViewType(viewType: ViewType) {
		this.viewType = viewType;

		if (viewType == ViewType.View3D) {
			this.setCameraMode(Potree.CameraMode.PERSPECTIVE);
			this.controls.rotationSpeed = 5;
			this.cadManager.convertMeasurements(false);

			//Show PCs
			this.pointCloudManager.showPointClouds();

			//Show Meshes
			this.meshManager.showMeshes();
		} else {
			this.setCameraMode(Potree.CameraMode.ORTHOGRAPHIC);
			this.scene.view.yaw = 0;
			this.scene.view.pitch = -Math.PI / 2;
			this.controls.rotationSpeed = 0;
			this.cadManager.convertMeasurements(true);

			//Hide PCs
			this.pointCloudManager.hidePointClouds();

			//Hide Meshes
			this.meshManager.hideMeshes();
		}

		//Update annotations scales, when going from ortho to perspective camera
		//because for orthographic camera scale is updated each frame to maintain the scale
		for (const annotation of this.annotations) {
			annotation.scale.set(1, 1, 1);
		}

		this.orthophotoManager.syncViewType(viewType);
		this.externalImageManager.setVisible(viewType == ViewType.View3D);

		this.volumeManager.syncViewType(this.viewType);

		this.profileManager.refreshHoverMarkersAfterViewChange();

		this.zoomToFit();
	}

	public addCadLayer(layerName: string): void {
		this.cadManager.addLayer(layerName);
	}

	public deleteCadLayer(layerName: string): void {
		this.cadManager.deleteLayer(layerName);
	}

	public addProfileLayer(layerName:string){
		this.profileManager.addLayer(layerName);
	}

	public deleteProfileLayer(layerName:string){
		this.profileManager.deleteLayer(layerName);
	}

	/**
	 * ZOOM controls
	 */

	zoomToFit(factor?: number) {
		let box: any = null;

		//Get bounding box, for 2d view calculate boundingbox for orthophoto and profiles
		if (this.viewType == ViewType.View2D) {
			box = this.orthophotoManager.getOrthophotoBoundsBox();
			if(!box && this.profiles.length > 0) {
				box = this.getBoundingBoxForObjects(this.profiles);
			}
			if(!box && this.volumes.length > 0){
				box = this.getBoundingBoxForObjects(this.volumes);
			}
		} else {
			// 3D mode: fit to actual 3D content first, fall back to ortho only if nothing else is visible.
			if (!box) {
				const meshBox = this.meshManager.getBoundingBox();
				if (meshBox) box = meshBox;
			}
			if(!box && this.volumes.length > 0){
				box = this.getBoundingBoxForObjects(this.volumes);
			}
			if(!box) {
				//Use pointcloud bounding box as last resort
				box = this.pointCloudManager.getBoundingBox();
			}

			if(!box && this.externalImageManager.markers.length > 0) {
				box = this.getBoundingBoxForObjects(this.externalImageManager.markers);
			}

			if(!box && this.profiles.length > 0) {
				box = this.getBoundingBoxForObjects(this.profiles);
			}
			
			if(!box && this.measurements.length > 0){
				box = this.getBoundingBoxForMeasurements(this.measurements);
			}

			if(!box){
				box = this.getBoundingBoxForMeasurements(this.unifiedLineMeasurements);
			}

			// Fallback: if no 3D content is visible but an orthophoto is, use its bounds.
			if (!box) {
				box = this.orthophotoManager.getVisibleOrthophotoBox();
			}
		}

		if(box){
			this.fitToScreen(box, factor);
		}else{
			console.warn("No object to that we could zoom to fit!");
		}
	}

	zoomToFitLayer(layerItem:LayerItem){
		let box: any = null;
		let object: any = null;

		switch(layerItem.layerType){
			case LayerItemType.Orthophoto:{
				box = this.orthophotoManager.getBoxForId(layerItem.id);
				break;
			}
			case LayerItemType.Cad:{
				const measurementObjects = this.measurements.filter((m)=>m.layerName == layerItem.name);
				const unifiedLineMeasurementObjects = this.unifiedLineMeasurements.filter((m)=>m.layerName == layerItem.name);
				const objects = [...measurementObjects, ...unifiedLineMeasurementObjects];
				if(objects.length){
					box = this.getBoundingBoxForMeasurements(objects);
				}
				break;
			}
			case LayerItemType.Annotations:
				object = this.annotations.find((m)=>m.uuid == layerItem.id);
				if(object){
					box = new THREE.Box3().setFromCenterAndSize(object.position,new Vector3(20,20,20));
				}
				break;
			case LayerItemType.Meshes:
				object = this.meshManager.findMeshById(layerItem.id);
				if(object){
					box = new THREE.Box3().setFromObject(object as any);
				}
				break;
			case LayerItemType.PointClouds:
				object = this.pointCloudManager.findById(layerItem.id);
				if(object){
					box = Potree.Utils.computeTransformedBoundingBox(object.boundingBox, object.matrixWorld);
				}
				break;
			case LayerItemType.Profiles:
				// Support zoom-to-fit for parent profile groups and individual child profiles
				if (layerItem.children && layerItem.children.length) {
					// Collect all THREE objects for children in this group
					const objs = this.profiles.filter(p => layerItem.children!.some(c => c.id === p.uuid));
					if (objs.length) {
						box = this.getBoundingBoxForObjects(objs);
					}
				} else {
					object = this.profiles.find((m)=>m.uuid == layerItem.id || m.userData.layerName == layerItem.name || m.name == layerItem.name);
					if(object){
						box = new THREE.Box3().setFromObject(object as any);						
					}
				}
				break;
			case LayerItemType.Volumes: {
				object = this.volumes.find((m) => m.uuid == layerItem.id);
				if (object) {
					box = this.getBoundingBoxForObjects([object]);
				}
				break;
			}
		}

		if(box){
			this.fitToScreen(box);
		}else{
			console.warn("No object to that we could zoom to fit!");
		}
	}

	getBoundingBoxForObjects(objects: any[]): THREE.Box3 | null{
		if (objects.length === 0) {
			return null;
		}

		// Aggregate world-space bounding box across all provided objects
		const aggregated = new THREE.Box3();
		let hasAny = false;

		for (let i = 0; i < objects.length; i++) {
			const obj = objects[i];
			let b: THREE.Box3 | null = null;

			try {
				// Ensure transforms are current before computing boxes
				if (obj.parent) {
					obj.parent.updateMatrixWorld(true);
				}
				obj.updateMatrixWorld(true);
			} catch {
				// Ignore transform update issues and fall back to existing matrices
			}

			// Pointclouds expose visibleBounds which need a special transform
			if (obj.visibleBounds) {
				try {
					obj.updateVisibleBounds();
					const bb = Potree.Utils.computeTransformedBoundingBox(obj.visibleBounds, obj.matrixWorld);
					if (bb && !bb.isEmpty()) {
						b = bb;
					}
				} catch {
					// Fallback below
				}
			}

			if (!b) {
				// Check if this is a volume object
				if (obj.parent?.name == "VolumesGroup") {
					b = this.volumeManager.getBoundingBoxForVolume(obj);
				} else {
					try {
						b = new THREE.Box3().setFromObject(obj);
					} catch {
						b = null;
					}
				}
			}

			if (b && !b.isEmpty()) {
				if (!hasAny) {
					aggregated.copy(b);
					hasAny = true;
				} else {
					aggregated.union(b);
				}
			}
		}

		return hasAny ? aggregated : null;
	}

	getBoundingBoxForMeasurements(measurements: any[]): THREE.Box3 | null{
		if(measurements.length === 0){
			return null;
		}
		const points: any[] = [];
		for(const measurement of measurements){
			for(const point of measurement.points){
				points.push(point.position);
			}
		}
		const box = new THREE.Box3();
		box.setFromPoints(points);
		return box;
	}

	fitToScreen(boundingBox: any, factor?: number) {
		// Provide a sensible default margin. For perspective, > 1 pulls back a bit.
		const margin = factor ?? 1.25;
		const animationDuration = 0;

		if (this.viewType === ViewType.View2D) {
			// Orthographic top-down fitting: adjust view radius to fit XY extents
			const center = new THREE.Vector3();
			boundingBox.getCenter(center);
			// Compute extents in world XY
			const size = new THREE.Vector3();
			boundingBox.getSize(size);
			const width = Math.max(1e-6, Math.abs(size.x));
			const height = Math.max(1e-6, Math.abs(size.y));

			// Get aspect from render area
			const w = (this.domElement as HTMLDivElement).clientWidth || 1;
			const h = (this.domElement as HTMLDivElement).clientHeight || 1;
			const aspect = w / h;

			// Potree sets: left/right = +-radius, top/bottom = +-radius/aspect
			// So required radius to fit is max(width/2, height/2 * aspect)
			const requiredRadius = Math.max(width * 0.5, height * 0.5 * aspect) * margin;
			this.scene.view.radius = Math.max(requiredRadius, 1e-3);

			// Keep 2D top-down orientation; do not rotate yaw on fit
			this.scene.view.pitch = -Math.PI / 2;
			// Compute new camera position so that pivot stays at center
			const dir = this.scene.view.direction.clone(); // will be (0,0,-1) with top-down pitch
			const camPos = center.clone().sub(dir.multiplyScalar(this.scene.view.radius));
			this.scene.view.position.copy(camPos);
			this.controls.stop();
			return;
		}

		// Perspective fitting: use Potree zoomTo with a bounding box node
		const node = new THREE.Object3D();
		(node as any).boundingBox = boundingBox;
		this.zoomTo(node, margin, animationDuration);
		this.controls.stop();
	}

	zoomIn() {
		//TODO detect when zoom inverses
		this.changeZoom(0.5);
	}

	zoomOut() {
		this.changeZoom(1.5);
	}

	private async changeZoom(delta: number){
		const targetRadius = Math.max(this.scene.view.radius * delta, 0.2);
		const point = await this.raycastScreenPositionToSurfaceOrPointcloud(new Vector2(0,0));
		if (this.externalImageManager.isActive) return;

		const d = this.scene.view.direction.multiplyScalar(-1);
		const cameraTargetPosition = new THREE.Vector3().addVectors(point, d.multiplyScalar(targetRadius));

		const targetPos = cameraTargetPosition.clone();

		this.scene.view.position.x = targetPos.x;
		this.scene.view.position.y = targetPos.y;
		this.scene.view.position.z = targetPos.z;

		this.scene.view.radius = targetRadius;
	}

	private startLineDrawing(lineType: Tools = Tools.Line): void {
		if (this.cadManager) {
			this.cadManager.startLineDrawing(lineType);
		}
	}

	private stopLineDrawing(removeIfIncomplete = true): void {
		if (this.cadManager) {
			this.cadManager.stopLineDrawing(removeIfIncomplete);
		}
	}

	private createNewAnnotation(): void {
		if (!this.cadManager) {
			console.warn("CadManager not initialized yet");
			return;
		}
		const annotation = this.cadManager.createAnnotation(this.intersectionPoint);
		this.movingAnnotation = annotation;
		this.movingAnnotation.selected = true;
		this.selectedElement = annotation;
		this.onSelectedElementChanged.emit(annotation);
		this.updateLayers();
	}

	async loadPointcloud(url: string, name: string, uuid: string): Promise<any> {
		return this.pointCloudManager.loadPointcloud(url, name, uuid);
	}

	loadModel(url: string, name: string, uuid: string, visible = false, transformationUrl: string): Promise<THREE.Object3D> {
		return this.meshManager.loadModel(url, name, uuid, visible, transformationUrl);
	}

	async loadDXF(url: string): Promise<boolean> {
		return this.cadManager.loadDXF(url);
	}

	async loadProfile(url: string): Promise<boolean> {
		return this.profileManager.load(url);
	}

	public updateGroundPosition(){
		//Updates ground position so that it is positioned on the botton of mesh or pointcloud bounding box
		//Prefer bounding box of mesh first (because it is less error, in PC sometimes a single point somwhere in a strange
		//position can cause incorrect bounding box)
		let bb = this.meshManager.getBoundingBox();
		if(!bb && this.pointCloudManager.pointclouds.length > 0){
			bb = this.getBoundingBox();
		}

		const groundPos = bb ? bb.min.z : 0;
		this.ground.setFromNormalAndCoplanarPoint(new THREE.Vector3(0,0,1), new THREE.Vector3(0,0,groundPos));
	}

	addMeasurement(measure: any): void {
		this.cadManager.addMeasurement(measure);
	}

	addUnifiedLineMeasurement(measure: any): void {
		this.cadManager.addUnifiedLineMeasurement(measure);
	}

	removeMeasurement(measure: any): void {
		this.cadManager.removeMeasurement(measure);
	}

	removeUnifiedLineMeasurement(measure: any): void {
		this.cadManager.removeUnifiedLineMeasurement(measure);
	}

	parseUnitFromWKT(wkt: string): string{
		if(!wkt || wkt.length == 0){
			return "";
		}
		try {
			const p = proj4(wkt)?.oProj;
			const projUnit = p.units ? p.units.toLowerCase() : "";
			let unit = "";
			switch(projUnit){
				case "foot_us":
					unit = "ftUS";
					break;
				case "meter":
				case "metre":
					unit = "m";
					break;
				case "foot":
					unit = "ft";
					break
				case "yard":
					unit = "yd";
					break;
				case "mile":
					unit = "mi";
					break;
			}
			return unit;
		} catch (error) {
			console.error("Error parsing coordinate system:", error);
			return "";
		}
	}

	getUnit(): string {
		return this.unit;
	}

	private updateProfileUnits() {
		this.profileManager.updateUnits(this.unit);
	}

	setProjectWKTCoordinateSystem(coordinateSystem: string){
		this.projectCoordinateSystem = coordinateSystem;
		this.unit = this.parseUnitFromWKT(coordinateSystem);
		this.updateProfileUnits();
		this.updateMeasurementsCoordinateSystems();
		this.changeViewType(this.viewType);
	}

	loadOrthophoto(baseUrl: string, name: string, bounds: number[], minZoom:number, maxZoom: number, uuid: string) {
		this.orthophotoManager.loadOrthophoto(baseUrl, name, bounds, minZoom, maxZoom, uuid);
	}

	convertMeasurements(toMapCoordinates: boolean): void {
		this.cadManager.convertMeasurements(toMapCoordinates);
		this.cadManager.convertAnnotations(toMapCoordinates);
		this.volumeManager.convert(toMapCoordinates);
		this.profileManager.convert(toMapCoordinates);
	}

	updateMeasurementsCoordinateSystems(): void {
		this.cadManager.updateMeasurementsCoordinateSystems();
	}

	onPointerDown(event: any) {
		if (this.externalImageManager.isActive && !this.externalImageManager.isReady) return;
		const target = event.target as HTMLElement | null;
		if (target?.parentNode == this.domElement || this.externalImageManager.isInteractionTarget(target)) {
			this.isPointerDown = true;
			this.getMousePosition(event, this.startPosition);
			this.mousePosition.copy(this.startPosition);

			this.getMousePositionOnSurfaceOrPointcloud().then((intersection) => {
				if (intersection) {
					this.targetIntersectionPoint.copy(intersection);
					this.smoothIntersectionPoint.copy(intersection);
					this.lastValidIntersectionPoint.copy(this.smoothIntersectionPoint);
					this.externalImageManager.magnifier.markSnappedPointAvailable();
					if (!this.isAnimating) this.startSmoothAnimation();
				}
			});
		}
	}

	onPointerMove(event) {
		if (this.externalImageManager.isActive && !this.externalImageManager.isReady) return;
		this.getMousePosition(event, this.mousePosition);
		this.previousMousePosition.copy(this.mousePosition);
		this.externalImageManager.magnifier.updatePosition();
		// Rotating a panorama does not need a synchronous point-cloud GPU readback.
		if (this.externalImageManager.isActive && this.currentTool === Tools.Selection
			&& this.movingSphere == null && this.movingAnnotation == null) return;

		// Pointcloud picking does a GPU readback and is expensive - pointermove can fire far
		// more often than frames render, so only do it once per frame.
		if (this.raycastPointcloudThisFrame) return;
		this.raycastPointcloudThisFrame = true;

		this.getMousePositionOnSurfaceOrPointcloud().then(intersection => {
			if (intersection) {
				this.targetIntersectionPoint.copy(intersection);
				this.externalImageManager.magnifier.markSnappedPointAvailable();
				if (!this.isAnimating) this.startSmoothAnimation();
			}
		});
	}

	private startSmoothAnimation() {
		this.isAnimating = true;
		const animate = () => {
			if (!this.isAnimating) return;

			const direction = new THREE.Vector3().subVectors(this.targetIntersectionPoint, this.smoothIntersectionPoint);
			const distance = direction.length();
			
			if (distance > 0.001) {
				const speed = Math.max(1.0, distance * 1.2);
				
				direction.normalize().multiplyScalar(Math.min(distance, speed));
				
				this.smoothIntersectionPoint.add(direction);
				this.lastValidIntersectionPoint.copy(this.smoothIntersectionPoint);
				requestAnimationFrame(animate);
			} else {
				// We're close enough to the target
				this.smoothIntersectionPoint.copy(this.targetIntersectionPoint);
				this.lastValidIntersectionPoint.copy(this.smoothIntersectionPoint);
				this.isAnimating = false;
			}
		};
		requestAnimationFrame(animate);
	}

	getMousePosition(event: any, vector: any): { x: number; y: number } {
		const rect = this.domElement.getBoundingClientRect();
		const v = vector ? vector : new THREE.Vector3();
		
		let clientX: number;
		let clientY: number;

		// Handle touch events
		if (event.touches && event.touches.length > 0) {
			clientX = event.touches[0].clientX;
			clientY = event.touches[0].clientY;
		}
		// Handle mouse events
		else {
			clientX = event.clientX;
			clientY = event.clientY;
		}

		// Convert to normalized device coordinates (-1 to +1)
		v.x = ((clientX - rect.left) / rect.width) * 2 - 1;
		v.y = -((clientY - rect.top) / rect.height) * 2 + 1;

		return v;
	}

	onPointerUp(event: any) {
		const rect = this.domElement.getBoundingClientRect();
		let delta = this.startPosition.distanceTo(this.mousePosition);
		delta *= Math.max(rect.width, rect.height);
		this.wasClicked = this.isPointerDown && event.button == 0 && delta <= 2;
		this.wasRightClick = this.isPointerDown && event.button == 2 && delta <= 2;
		this.isPointerDown = false;
		if (this.suppressNextPointerClick) {
			this.wasClicked = false;
			this.wasRightClick = false;
			this.suppressNextPointerClick = false;
		}
		if (this.wasRightClick) {
			if (Date.now() - this.previousRightClickTime < 1000) {
				this.wasDoubleRightClick = true;
			}
			this.previousRightClickTime = Date.now();
		}
	}

	onKeyDown(event: any) {
		// Track Ctrl/Cmd key for multi-selection
		if (event.ctrlKey || event.metaKey) {
			this.isMultiSelectKeyPressed = true;
		}
		
		// Handle ESC key to deselect all profiles
		if (event.keyCode === 27 || event.key === "Escape") {
			this.deselectAllProfiles();
			event.preventDefault();
		}
	}

	onKeyUp(event:any){
		// Release multi-select key
		if (!event.ctrlKey && !event.metaKey) {
			this.isMultiSelectKeyPressed = false;
		}

		if(event.keyCode == 8){ // Backspace
			const measurement = this.createdMeasurement as any;
			if(measurement){
				if (measurement.points.length > 1) {
					measurement.removeMarker(measurement.points.length - 1);
				}
			}
		}
	}

	hoverObject(object: any, hover: boolean) {
		object.hover = hover;
	}

	selectObject(object: any, select: boolean) {
		object.selected = select;
	}

	setHelperPosition(_pos:Vector3){
		// Helper position functionality disabled
	}

	hideHelperPosition(){
		// Helper position functionality disabled
	}

	// Raycasters ignore Object3D.visible, so only accept visible hits within 15px of the pointer.
	private pickClosestToPointer(candidates: { intersection: THREE.Intersection; owner: any }[]): any {
		const camera = this.scene.getActiveCamera();
		const viewportSize = this.renderer.getSize(new THREE.Vector2());
		const projectedPoint = new THREE.Vector3();
		let best: any = null;
		let bestDistSq = 15 * 15;

		for (const { intersection, owner } of candidates) {
			if (!intersection.object.visible) continue;

			projectedPoint.copy(intersection.point).project(camera);
			const dx = (projectedPoint.x - this.mousePosition.x) * viewportSize.x * 0.5;
			const dy = (projectedPoint.y - this.mousePosition.y) * viewportSize.y * 0.5;
			const distSq = dx * dx + dy * dy;
			if (distSq < bestDistSq) {
				best = owner;
				bestDistSq = distSq;
			}
		}

		return best;
	}

	deselectElement(){
		if (this.selectedElement) {
			this.selectObject(this.selectedElement, false);
			this.hoverObject(this.selectedElement, false);
		}
		this.selectedElement = null;
		this.clearProfileMultiSelection();
		this.movingAnnotation = null;
		this.movingSphere = null;
	}

	private handleProfileMultiSelection(profile: Profile) {
		this.profileManager.handleMultiSelection(profile);
	}

	public clearProfileMultiSelection() {
		this.profileManager.clearMultiSelection();
	}

	public deselectAllProfiles() {
		this.profileManager.deselectAll();
	}

	private updateProfileLine2Resolution() {
		if (this.profileManager) {
			this.profileManager.updateLine2Resolution();
		}
	}

	public handleResize() {
		// Get the container size
		const containerEl = this.renderer.domElement.parentElement;
		if (!containerEl) return;
		
		const width = containerEl.clientWidth;
		const height = containerEl.clientHeight;
		
		// Resize renderer and update camera aspect ratio
		this.renderer.setSize(width, height);
		
		// Update camera aspect ratio if it's a perspective camera
		const camera = this.scene.getActiveCamera();
		if (camera.isPerspectiveCamera) {
			camera.aspect = width / height;
			camera.updateProjectionMatrix();
		}
		
		// Force an update to recalculate sizes for annotations, markers, etc.
		if (this.cadManager) {
			this.cadManager.updateMeasurements();
		}
		this.updateProfileLine2Resolution();
	}

	update(delta: number, timestamp: number) {
		this.raycastPointcloudThisFrame = false;
		super.update(delta, timestamp);
		if (this.cadManager) {
			this.cadManager.updateMeasurements();
		}
		this.updateProfileLine2Resolution();
		const camera = this.scene.getActiveCamera();

		//Update MapViews
		this.orthophotoManager.update(camera);

		//Limit orthographic camera
		if (camera.isOrthographicCamera) {
			//Updated annotation sizes when in orthographic mode
			const renderAreaSize = this.renderer.getSize(new THREE.Vector2());
			const clientWidth = renderAreaSize.width;
			const clientHeight = renderAreaSize.height;
			for (const annotation of this.annotations) {
				const distance = camera.position.distanceTo(annotation.getWorldPosition(new THREE.Vector3()));
				const pr = Potree.Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
				const scale = (70 / pr) * 12;

				annotation.scale.set(scale, scale, scale);
			}

			// Calculate scene bounds to handle objects at both positive and negative Z
			const sceneBBox = this.getBoundingBox();
			let minZ = -10000; // Default for orthophoto
			let maxZ = 10000;

			if (sceneBBox) {
				minZ = Math.min(sceneBBox.min.z, minZ);
				maxZ = Math.max(sceneBBox.max.z, maxZ);
			}

			// Position camera high enough to see everything
			const cameraZ = maxZ + 1000;
			this.scene.view.position.z = cameraZ;

			// Set near/far planes to encompass all objects including negative Z
			// Camera looks down (-Z), so near = distance to farthest object, far = distance to closest + margin
			camera.near = Math.max(0.1, cameraZ - maxZ - 100); // Small near for objects above camera
			camera.far = cameraZ - minZ + 100; // Far enough to see objects at negative Z
		}else{
			camera.near = 0.01;
		}

		// update the picking ray with the camera and pointer position
		this.raycaster.setFromCamera(this.mousePosition, camera);

		switch (this.currentTool) {
			case Tools.Height:
			case Tools.Point:
			case Tools.Polygon:
			case Tools.Circle:
			case Tools.Line:
				if (this.createdMeasurement && this.lastValidIntersectionPoint) {
					if (this.wasClicked && (this.createdMeasurement as any).maxMarkers === (this.createdMeasurement as any).points.length) {
						//Hide coordinates after point is dropped
						if(this.currentTool == Tools.Point){
							(this.createdMeasurement as any).showCoordinates = false;
						}
						this.stopLineDrawing(false);
						this.startLineDrawing(this.currentTool);
						break;
					}
					const pos = this.lastValidIntersectionPoint;
					const measurement = this.createdMeasurement as any;

					if (pos) {
						measurement.setPosition(measurement.points.length - 1, pos);
						this.setHelperPosition(pos);

						if (this.wasClicked) {
							measurement.addMarker(new THREE.Vector3(pos.x, pos.y, pos.z));
							this.externalImageManager.updateOverlayRenderStateFor(measurement);
						}
					}
				}
				if (this.wasRightClick) {
					this.stopLineDrawing(true);
					this.onToolCancel.emit();
				}
				break;
			case Tools.Selection:
				this.updateSelectionTool(true, true);

				break;
			case Tools.Annotation:
				this.updateSelectionTool(false, true);

				this.cadManager.updateAnnotationTool();
				break;
			default:
				break;
		}

		this.wasPointerDown = this.isPointerDown;
		this.wasClicked = false;
		this.wasRightClick = false;
		this.wasDoubleRightClick = false;

		if (this.volumeManager) {
			this.volumeManager.updateHoverTooltip();
		}
		// window.requestAnimationFrame(this.update.bind(this));
	}

	getMousePositionOnSurfaceOrPointcloud(): any {
		return this.raycastScreenPositionToSurfaceOrPointcloud(this.mousePosition);
	}

	// Position in this case is normalized in range [-1,1]
	async raycastScreenPositionToSurfaceOrPointcloud(position: Vector2):Promise<any>{
		//Convert screen vector [-1,1] to [0,screenWidth] [0,screenHeight] for intersection with pointclouds
		const camera = this.scene.getActiveCamera();
		const size = this.renderer.getSize(new THREE.Vector2());
		const screenPos = position.clone();
		screenPos.x = (screenPos.x * size.x * 0.5)+size.x * 0.5;
		screenPos.y = (screenPos.y * size.y * -0.5)+size.y * 0.5;

		const intersection = Potree.Utils.getMousePointCloudIntersection(
					screenPos,
					camera,
					this,
					this.scene.pointclouds);

		if (intersection) {
			return intersection.location;
		}

		this.raycaster.setFromCamera(position, camera);

		const volumeIntersection = await this.volumeManager.intersectVisibleMeshes(this.raycaster);
		if (volumeIntersection) return volumeIntersection;

		// Intersect meshes
		const meshIntersection = await this.meshManager.raycast(this.raycaster);
		if (meshIntersection) return meshIntersection;		

		//Intersect map
		const intersectionPoint = new THREE.Vector3();
		this.raycaster.ray.intersectPlane(this.ground, intersectionPoint);
		if (intersectionPoint) {
			return intersectionPoint;
		}

		return undefined;
	}

	updateSelectionTool(selectLines: boolean, selectAnnotations: boolean) {
		let didChange = false;
		let objectUnderMouse: any;
		let cursor = "default";

		const candidates: { intersection: THREE.Intersection; owner: any }[] = [];

		if (selectLines) {
			const allMeasurements = [...this.measurements, ...this.unifiedLineMeasurements];
			for (const intersection of this.raycaster.intersectObjects(allMeasurements, true)) {
				const owner = intersection.object.parent;
				const isArea = intersection.object.type === "Mesh" && intersection.object.name === "area";
				if ((owner instanceof Measure || owner instanceof UnifiedLineMeasure) && !isArea) {
					candidates.push({ intersection, owner });
				}
			}

			const profileHit = this.raycaster.intersectObjects(this.profiles, true)[0];
			if (profileHit) candidates.push({ intersection: profileHit, owner: profileHit.object });
		}

		if (selectAnnotations) {
			for (const intersection of this.raycaster.intersectObjects(this.annotations, true)) {
				if (intersection.object.parent instanceof Annotation) {
					candidates.push({ intersection, owner: intersection.object.parent });
				}
			}
		}

		const markerHit = this.raycaster.intersectObjects(this.externalImageManager.pickableMarkers, false)[0];
		if (markerHit) candidates.push({ intersection: markerHit, owner: markerHit.object });

		objectUnderMouse = this.pickClosestToPointer(candidates);

		// Handle profile hover detection with more precise threshold (separate from selection)
		this.profileManager.handleHoverDetection();

		// Prioritize the hovered profile for selection over regular raycasting
		const hoveredProfile = this.profileManager.getHoveredProfile();
		if (hoveredProfile) {
			// Override any profile that was selected by regular raycasting
			if (objectUnderMouse && objectUnderMouse instanceof Profile) {
				objectUnderMouse = hoveredProfile;
			} else if (!objectUnderMouse) {
				objectUnderMouse = hoveredProfile;
			}
		}

		if (objectUnderMouse?.userData?.externalImage) {
			cursor = "pointer";
		}
		this.externalImageManager.updateMarkerHover(objectUnderMouse?.userData?.externalImage ? objectUnderMouse : null);

		//Unhighlight previously selected element
		if (this.highlightedElement != objectUnderMouse) {
			if (this.highlightedElement && (!this.selectedElement || this.highlightedElement.uuid != this.selectedElement.uuid)) {
				this.hoverObject(this.highlightedElement, false);
			}
			this.highlightedElement = objectUnderMouse;
			if (this.highlightedElement) {
				this.hoverObject(this.highlightedElement, true);
			}
		}

		const el = this.selectedElement as any;

		// ANNOTATION MOVEMENT
		if(el instanceof Annotation && el.selected){
			//Mark annotation for moving
			if(!this.movingAnnotation && this.selectedElement == objectUnderMouse && this.isPointerDown && !this.wasPointerDown){
				this.movingAnnotation = objectUnderMouse;
				//Disable camera dragging
				this.inputHandler.drag = null;
			}else if(this.movingAnnotation == null && el && !this.isPointerDown){
				cursor = "grab";
			}

			//Select element if it is not selected
			if (this.movingAnnotation && this.lastValidIntersectionPoint) {
				const pos = this.lastValidIntersectionPoint
				if (pos) {
					this.setHelperPosition(pos);
					if(this.viewType == ViewType.View2D){ // Do not move points by Z axis
						pos.z = this.movingAnnotation.position.z;
					}
					this.movingAnnotation.position.set(pos.x, pos.y, pos.z);
				}

				if (this.wasPointerDown && !this.isPointerDown) {
					this.movingAnnotation = null;
				}
			}else{
				this.hideHelperPosition();
			}	
		} else if (el && el.selected && el.spheres && el.spheres.length > 0) { //LINE CONTROL POINT movement
			//Handle interactive points for selected element
			if (this.movingSphere == null) {
				// Find all sphere intersections
				const intersections = this.raycaster.intersectObjects(el.spheres, true);
				const intersection = intersections[0];
				
				if (intersection && this.isPointerDown && !this.wasPointerDown) {
					this.movingSphere = intersection.object;
					
					// Store intersection data for instanced mesh
					if (intersection.instanceId !== undefined) {
						this.movingSphere.userData.instanceId = intersection.instanceId;
					}
				} else if(this.movingSphere == null && intersection && !this.isPointerDown) {
					cursor = "grab";
				}	
				if (this.movingSphere) {
					//Disable camera dragging
					this.inputHandler.drag = null;
				}
			}

			if (this.movingSphere && this.lastValidIntersectionPoint) {
				const pos = this.lastValidIntersectionPoint
				let index = -1;
				
				// Handle instanced mesh indexes differently
				if (el instanceof UnifiedLineMeasure) {
					index = this.movingSphere.userData.instanceId !== undefined ? 
						this.movingSphere.userData.instanceId : -1;
				} else {
					index = el.spheres.indexOf(this.movingSphere);
				}
				
				if (pos) {
					this.setHelperPosition(pos);

					if(this.viewType == ViewType.View2D){ // Do not move points by Z axis
						pos.z = this.movingSphere.position.z;
					}

					//Set first index for single point measurements
					if(index < 0 && el.maxMarkers == 1){
						index = 0;
					}
					el.setPosition(index, pos);
				}

				if (this.wasPointerDown && !this.isPointerDown) {
					this.movingSphere = null;
				}
			}else{
				this.hideHelperPosition();
			}

			if (this.wasClicked) {
				this.selectObject(this.selectedElement, false);
				this.hoverObject(this.selectedElement, false);
				this.selectedElement = null;
				didChange = true;
			} 
		}

		//Highlight new line or annotation
		if (this.wasDoubleLeftClick) {
			if (objectUnderMouse) {
				// Handle profile double-click zoom
				if (objectUnderMouse instanceof Profile) {
					this.zoomToProfile(objectUnderMouse);
				}
			}
		} else if (this.wasClicked) {
			if (objectUnderMouse) {
				if (objectUnderMouse.userData?.externalImage) {
					this.onExternalImageOpen.emit(objectUnderMouse.userData.externalImage as ExternalImage);
					this.deselectElement();
					didChange = true;
				}
				// Handle profile selection
				else if (objectUnderMouse instanceof Profile) {
					const result = this.profileManager.handleProfileClick(objectUnderMouse, this.isMultiSelectKeyPressed);
					if (result.selectedElement) {
						this.selectedElement = result.selectedElement;
					}
					didChange = result.didChange;
				} else {
					// Regular single selection for non-profiles
					if (this.selectedElement) {
						this.selectObject(this.selectedElement, false);
						this.hoverObject(this.selectedElement, false);
					}
					// Clear multi-selection when doing regular selection
					this.clearProfileMultiSelection();
					this.selectedElement = objectUnderMouse;
					this.selectObject(this.selectedElement, true);
					didChange = true;
				}
			} else {
				this.deselectElement();
				didChange = true;
			}
		}

		this.domElement.style.cursor = this.volumeManager.getCursorOverride() ?? cursor;

		if (didChange) {
			this.onSelectedElementChanged.emit(this.selectedElement);
		}
	}

	public suppressCurrentPointerClick(): void {
		this.suppressNextPointerClick = true;
		this.isPointerDown = false;
	}

	public isCadDragTargetUnderPointer(clientX: number, clientY: number): boolean {
		if (this.movingSphere != null || this.movingAnnotation != null) return true;

		const el = this.selectedElement as any;
		if (!el?.selected) return false;
		const targets = el instanceof Annotation ? [el] : el.spheres;
		if (!targets?.length) return false;

		const rect = this.renderer.domElement.getBoundingClientRect();
		this.raycaster.setFromCamera(new THREE.Vector2(
			((clientX - rect.left) / rect.width) * 2 - 1,
			-((clientY - rect.top) / rect.height) * 2 + 1,
		), this.scene.getActiveCamera());
		return this.raycaster.intersectObjects(targets, true).some((hit) => hit.object.visible);
	}

	public updateProfileVisibility(profileId: string, visible: boolean) {
		this.profileManager.updateVisibility(profileId, visible);
	}

	public updateLayerObjectVisibility() {
		this.profileManager.updateLayerObjectVisibility();
	}

	/**
	 * Show/hide a hover marker on a profile polyline.
	 * Pass position as THREE.Vector3 or {x,y,z}. Pass null/undefined to hide.
	 */
    public setProfileHoverMarker(
        profileId: string,
        position?: THREE.Vector3 | { x: number; y: number; z: number } | null
    ) {
		this.profileManager.setHoverMarker(profileId, position);
    }

	/** Hide and remove all hover markers (or only those passed). */
	public clearProfileHoverMarkers(profileIds?: string[]) {
		this.profileManager.clearHoverMarkers(profileIds);
	}

	public zoomToProfile(profile: any) {
		this.profileManager.zoomToProfile(profile);
	}

	async loadVolume(url: string, name: string, uuid: string, visible = false, files: string[], metadata: VolumeMetadata): Promise<any> {
		return this.volumeManager.load(url, name, uuid, visible, files, metadata);
	}

	// Update overlay (selection polygon) color for a specific volume
	public setVolumeOverlayColor(volumeId: string, color: string | number | THREE.Color) {
		this.volumeManager.setOverlayColor(volumeId, color);
	}

	// Check if heatmap is currently enabled for a volume
	public isVolumeHeatmapEnabled(volumeId: string): boolean {
		return this.volumeManager.isHeatmapEnabled(volumeId);
	}

	// Enable or disable heatmap for a volume
	public async setVolumeHeatmapEnabled(volumeId: string, enabled: boolean): Promise<void> {
		return this.volumeManager.setHeatmapEnabled(volumeId, enabled);
	}

	// Rebuild connectors for every loaded volume by reprojecting polygon vertices
	public async rebuildAllVolumeConnectors(): Promise<void> {
		return this.volumeManager.rebuildAllConnectors();
	}

	removeVolume(volume: any) {
		this.volumeManager.remove(volume);
	}

	public cleanup(){
		this.externalImageManager.cleanup();
		//Remove all orthophoto maps
		this.orthophotoManager.cleanup();

		//Annotations
		while(this.annotations.length > 0){
			const e = this.annotations[0];
			this.annotations.splice(0,1);
			if(e.parent){
				e.parent.remove(e);
			}
		}	
		//Meshes
		this.meshManager.cleanup();

		this.volumeManager.cleanup();

		//Remove measurements
		this.cadManager.cleanup();

		//Remove profiles
		this.profileManager.cleanup();

		//Remove pointclouds
		this.pointCloudManager.cleanup();
	}

}
